import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import ColorLensRoundedIcon from "@mui/icons-material/ColorLensRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate } from "react-router-dom";
import { landingAssetsApi } from "../../api/landingAssets";
import { settingsApi } from "../../api/settings";
import PendingAssetField from "../../components/dashboard/landingEditor/PendingAssetField";
import ProjectDeploymentCard from "../../components/dashboard/ProjectDeploymentCard";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { assetValueFromResponse } from "../../utils/landingAssets";
import {
  isOnboardingSoundEnabled,
  playOnboardingSound,
  setOnboardingSoundEnabled,
} from "../../utils/onboardingSound";
import { isSuperAdmin } from "../../utils/roles";
import {
  activeModuleLabels,
  BILLING_PLAN_LABELS,
  modulesForPlan,
  normalizeProjectModules,
  normalizeProjectPlanCode,
} from "../../utils/projectModules";

const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  purple: "#6957E8",
  yellow: "#FFC857",
  green: "#26A269",
  blue: "#2F80ED",
};
const emptyBranding = { logo: null, compactLogo: null, favicon: null };

function emptyForm(email = "", plan = "catalogo") {
  return {
    name: "",
    slug: "",
    ownerEmail: email,
    plan,
    domains: "",
  };
}

function makeSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function uploadInitialBranding(projectId, branding, businessName) {
  const uploads = [
    {
      file: branding.logo,
      field: "logoUrl",
      sectionKey: "branding",
      assetKey: "logo",
    },
    {
      file: branding.compactLogo,
      field: "compactLogoUrl",
      sectionKey: "branding",
      assetKey: "compact-logo",
    },
    {
      file: branding.favicon,
      field: "faviconUrl",
      sectionKey: "branding",
      assetKey: "favicon",
    },
  ].filter((item) => item.file);

  if (!uploads.length) return;

  const uploadedPaths = [];
  const theme = {};

  try {
    for (const item of uploads) {
      const response = await landingAssetsApi.upload(projectId, item.file, {
        pageKey: "home",
        sectionKey: item.sectionKey,
        assetKey: item.assetKey,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El backend no devolvió la ruta del archivo");
      uploadedPaths.push(value);
      theme[item.field] = value;
    }

    await settingsApi.update(projectId, {
      business: { name: businessName },
      seo: { title: businessName },
      theme,
    });
  } catch (error) {
    await Promise.all(
      uploadedPaths.map((path) => landingAssetsApi.remove(projectId, path).catch(() => null))
    );
    throw error;
  }
}

function StepSignal({ number, title, text, color, complete, active }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          bgcolor: complete ? color : active ? `${color}1F` : "rgba(25,36,47,.06)",
          color: complete ? "#fff" : active ? color : "rgba(25,36,47,.38)",
          fontWeight: 1000,
          border: active && !complete ? `2px solid ${color}` : "2px solid transparent",
        }}
      >
        {complete ? <CheckCircleRoundedIcon sx={{ fontSize: 21 }} /> : number}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: "block", color: active || complete ? color : "rgba(25,36,47,.42)", fontWeight: 1000, letterSpacing: .55 }}>
          PASO {number}
        </Typography>
        <Typography noWrap sx={{ color: BRAND.dark, fontWeight: 950, fontSize: ".9rem" }}>{title}</Typography>
        {text ? <Typography noWrap variant="caption" sx={{ display: { xs: "none", lg: "block" }, color: "rgba(25,36,47,.52)" }}>{text}</Typography> : null}
      </Box>
    </Stack>
  );
}

function AutomaticItem({ icon, title, text, color, background }) {
  return (
    <Stack direction="row" spacing={1.3} alignItems="flex-start">
      <Box sx={{ width: 42, height: 42, borderRadius: 3, display: "grid", placeItems: "center", flexShrink: 0, color, bgcolor: background }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{title}</Typography>
        <Typography variant="body2" sx={{ mt: .2, color: "rgba(25,36,47,.6)", lineHeight: 1.5 }}>{text}</Typography>
      </Box>
    </Stack>
  );
}

function FirstProjectOnboarding({
  form,
  branding,
  creating,
  createStatus,
  error,
  soundEnabled,
  onSoundToggle,
  onChange,
  onBrandingChange,
  onSubmit,
}) {
  const businessReady = Boolean(form.name.trim() && form.slug.trim());
  const brandingReady = Boolean(branding.logo || branding.compactLogo || branding.favicon);
  const progress = creating ? (createStatus.includes("Render") ? 78 : createStatus.includes("logos") ? 58 : 34) : businessReady ? (brandingReady ? 66 : 44) : 12;
  const addressPreview = form.slug ? `${form.slug}.onrender.com` : "tu-negocio.onrender.com";

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: 4, md: 6 },
          p: { xs: 3, sm: 4, md: 5 },
          background: "linear-gradient(120deg, #FFF3E8 0%, #FFF9F4 48%, #F1EEFF 100%)",
          border: "1px solid rgba(243,90,58,.12)",
          "@keyframes onboardingBubble": {
            "0%, 100%": { transform: "translateY(0) rotate(1deg)" },
            "50%": { transform: "translateY(-7px) rotate(-1deg)" },
          },
        }}
      >
        <Box sx={{ position: "absolute", width: 240, height: 240, borderRadius: "50%", bgcolor: "rgba(105,87,232,.08)", right: -80, top: -110 }} />
        <Box sx={{ position: "absolute", width: 150, height: 150, borderRadius: "50%", bgcolor: "rgba(255,200,87,.18)", right: 220, bottom: -100 }} />

        <Stack direction={{ xs: "column", md: "row" }} spacing={3} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ position: "relative" }}>
          <Box sx={{ maxWidth: 760 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip icon={<AutoAwesomeRoundedIcon />} label="ACTIVACIÓN GRATUITA" sx={{ bgcolor: "#fff", color: BRAND.orange, fontWeight: 1000, boxShadow: "0 8px 24px rgba(25,36,47,.07)", "& .MuiChip-icon": { color: BRAND.yellow } }} />
              <Chip label="Sin tarjeta" variant="outlined" sx={{ bgcolor: "rgba(255,255,255,.55)", borderColor: "rgba(25,36,47,.12)", fontWeight: 850 }} />
            </Stack>
            <Typography variant="h2" sx={{ mt: 2, color: BRAND.dark, fontSize: { xs: "2.15rem", md: "3.35rem" }, lineHeight: 1.02, fontWeight: 1000, letterSpacing: -1.8 }}>
              Vamos a crear tu primera web
            </Typography>
            <Typography sx={{ mt: 1.5, color: "rgba(25,36,47,.65)", fontSize: { xs: "1rem", md: "1.08rem" }, lineHeight: 1.65, maxWidth: 680 }}>
              Completá lo esencial y nosotros preparamos el proyecto, la página editable y su dirección pública. Todo lo técnico sucede automáticamente.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.2} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                position: "relative",
                p: 2,
                pr: 3,
                borderRadius: "22px 22px 5px 22px",
                bgcolor: BRAND.dark,
                color: "#fff",
                animation: "onboardingBubble 4s ease-in-out infinite",
                boxShadow: "0 18px 38px rgba(25,36,47,.18)",
              }}
            >
              <Typography sx={{ fontWeight: 1000 }}>¡Tu web está a 3 pasos! 🎈</Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.68)" }}>Te señalamos qué hacer en cada uno.</Typography>
            </Paper>
            <Tooltip title={soundEnabled ? "Desactivar sonidos suaves" : "Activar sonidos suaves"}>
              <IconButton
                onClick={onSoundToggle}
                sx={{ bgcolor: "#fff", color: soundEnabled ? BRAND.purple : "rgba(25,36,47,.45)", boxShadow: "0 10px 28px rgba(25,36,47,.1)", "&:hover": { bgcolor: "#fff" } }}
              >
                {soundEnabled ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)", bgcolor: "#fff" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <StepSignal number={1} title="Tu negocio" text="Nombre y dirección" color={BRAND.orange} active={!businessReady} complete={businessReady} />
          <Box sx={{ display: { xs: "none", md: "block" }, width: 42, height: 2, bgcolor: businessReady ? "rgba(38,162,105,.32)" : "rgba(25,36,47,.08)" }} />
          <StepSignal number={2} title="Tu identidad" text="Logo opcional" color={BRAND.purple} active={businessReady && !creating} complete={brandingReady} />
          <Box sx={{ display: { xs: "none", md: "block" }, width: 42, height: 2, bgcolor: creating ? "rgba(38,162,105,.32)" : "rgba(25,36,47,.08)" }} />
          <StepSignal number={3} title="Publicación" text="La hacemos nosotros" color={BRAND.green} active={creating} complete={false} />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mt: 2.2, height: 7, borderRadius: 999, bgcolor: "rgba(25,36,47,.06)", "& .MuiLinearProgress-bar": { borderRadius: 999, background: "linear-gradient(90deg, #F35A3A, #6957E8)" } }}
        />
      </Paper>

      {error ? <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert> : null}

      <Box component="form" onSubmit={onSubmit}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={7}>
            <Stack spacing={2.5}>
              <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 5, border: "1px solid rgba(243,90,58,.18)", boxShadow: "0 18px 50px rgba(25,36,47,.06)" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 3.2, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.11)", color: BRAND.orange }}>
                    <StorefrontRoundedIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 1000, letterSpacing: .8 }}>PASO 1 · OBLIGATORIO</Typography>
                    <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 1000 }}>¿Cómo se llama tu negocio?</Typography>
                  </Box>
                </Stack>

                <Stack spacing={2.2}>
                  <TextField
                    label="Nombre del negocio"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    autoFocus
                    fullWidth
                    placeholder="Ej: Repuestos RR"
                    helperText="Este nombre aparecerá en el panel y será la base de tu página."
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                  <TextField
                    label="Dirección inicial de la web"
                    name="slug"
                    value={form.slug}
                    onChange={onChange}
                    required
                    fullWidth
                    placeholder="repuestos-rr"
                    helperText="Podés corregirla ahora. Usá letras, números y guiones."
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />

                  <Paper elevation={0} sx={{ p: 2, borderRadius: 3.5, bgcolor: "rgba(47,128,237,.055)", border: "1px dashed rgba(47,128,237,.28)" }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <LanguageRoundedIcon sx={{ color: BRAND.blue }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: BRAND.blue, fontWeight: 1000 }}>VISTA PREVIA DE LA DIRECCIÓN</Typography>
                        <Typography noWrap sx={{ color: BRAND.dark, fontWeight: 950 }}>{addressPreview}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.52)" }}>Render asignará el dominio definitivo y lo verás en tu panel.</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 5, border: "1px solid rgba(105,87,232,.17)", boxShadow: "0 18px 50px rgba(25,36,47,.045)" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 2.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 48, height: 48, borderRadius: 3.2, display: "grid", placeItems: "center", bgcolor: "rgba(105,87,232,.1)", color: BRAND.purple }}>
                      <ColorLensRoundedIcon />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: BRAND.purple, fontWeight: 1000, letterSpacing: .8 }}>PASO 2 · OPCIONAL</Typography>
                      <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 1000 }}>Sumá tu identidad</Typography>
                    </Box>
                  </Stack>
                  <Chip label="Podés hacerlo después" size="small" sx={{ alignSelf: { xs: "flex-start", sm: "center" }, bgcolor: "rgba(105,87,232,.08)", color: BRAND.purple, fontWeight: 900 }} />
                </Stack>

                <Typography sx={{ mb: 2.2, color: "rgba(25,36,47,.61)", lineHeight: 1.6 }}>
                  Cargá los archivos que ya tengas. No hace falta frenar la activación: el editor visual te permitirá reemplazarlos cuando quieras.
                </Typography>
                <Stack spacing={1.7}>
                  <PendingAssetField
                    label="Logo principal"
                    file={branding.logo}
                    disabled={creating}
                    helperText="SVG, PNG, WebP o JPG."
                    onChange={(file) => onBrandingChange("logo", file)}
                  />
                  <PendingAssetField
                    label="Logo compacto"
                    file={branding.compactLogo}
                    disabled={creating}
                    helperText="Versión cuadrada o reducida para celular."
                    onChange={(file) => onBrandingChange("compactLogo", file)}
                  />
                  <PendingAssetField
                    label="Favicon"
                    file={branding.favicon}
                    disabled={creating}
                    accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                    helperText="Ícono pequeño de la pestaña."
                    onChange={(file) => onBrandingChange("favicon", file)}
                  />
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 5, bgcolor: BRAND.dark, color: "#fff", position: { lg: "sticky" }, top: { lg: 24 }, overflow: "hidden", boxShadow: "0 24px 65px rgba(25,36,47,.18)" }}>
              <Box sx={{ position: "absolute", width: 190, height: 190, borderRadius: "50%", bgcolor: "rgba(105,87,232,.18)", right: -80, top: -75 }} />
              <Box sx={{ position: "relative" }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box sx={{ width: 48, height: 48, borderRadius: 3.2, display: "grid", placeItems: "center", bgcolor: "rgba(38,162,105,.16)", color: "#65D69B" }}>
                    <RocketLaunchRoundedIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#65D69B", fontWeight: 1000, letterSpacing: .8 }}>PASO 3 · AUTOMÁTICO</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 1000 }}>WebProfit se encarga</Typography>
                  </Box>
                </Stack>

                <Stack spacing={2.2} sx={{ mt: 3 }}>
                  <AutomaticItem icon={<StorefrontRoundedIcon />} title="Crea tu proyecto" text="Preparamos el espacio privado y la configuración inicial." color="#FF9B85" background="rgba(243,90,58,.16)" />
                  <AutomaticItem icon={<CloudDoneRoundedIcon />} title="Publica la página" text="Render construye el sitio y asigna el dominio público." color="#65D69B" background="rgba(38,162,105,.16)" />
                  <AutomaticItem icon={<ColorLensRoundedIcon />} title="Abre el editor" text="Entrás directo a personalizar textos, imágenes y secciones." color="#B8AEFF" background="rgba(105,87,232,.2)" />
                </Stack>

                <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,.1)" }} />

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
                  <CheckCircleRoundedIcon sx={{ color: "#65D69B", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,.78)", fontWeight: 800 }}>No tenés que configurar Render</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.8 }}>
                  <CheckCircleRoundedIcon sx={{ color: "#65D69B", fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,.78)", fontWeight: 800 }}>Podés editar la landing durante el trial</Typography>
                </Stack>

                {createStatus ? (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,.92)" }}>
                    {createStatus}
                  </Alert>
                ) : null}

                <Button
                  type="submit"
                  fullWidth
                  disabled={creating || !businessReady}
                  size="large"
                  variant="contained"
                  endIcon={!creating ? <ArrowForwardRoundedIcon /> : null}
                  sx={{
                    bgcolor: BRAND.orange,
                    borderRadius: 999,
                    py: 1.55,
                    px: 2.5,
                    textTransform: "none",
                    fontWeight: 1000,
                    fontSize: "1rem",
                    boxShadow: "0 16px 34px rgba(243,90,58,.32)",
                    "&:hover": { bgcolor: "#dd472b", transform: "translateY(-2px)" },
                    "&.Mui-disabled": { bgcolor: "rgba(255,255,255,.12)", color: "rgba(255,255,255,.38)" },
                    transition: "all .2s ease",
                  }}
                >
                  {creating ? "Estamos activando tu web..." : "¡Activar mi proyecto web gratis!"}
                </Button>
                {!businessReady ? (
                  <Typography variant="caption" sx={{ display: "block", mt: 1.2, textAlign: "center", color: "rgba(255,255,255,.52)" }}>
                    Completá el nombre y la dirección para continuar.
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ display: "block", mt: 1.2, textAlign: "center", color: "rgba(255,255,255,.52)" }}>
                    Al continuar, comenzará la creación automática.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);
  const {
    projects,
    activeProject,
    projectCreation,
    setActiveProject,
    createProject,
    loadProjects,
    loading,
    error,
  } = useProjects();
  const [form, setForm] = React.useState(() => emptyForm(
    user?.email || "",
    superAdmin ? "catalogo" : "landing",
  ));
  const [branding, setBranding] = React.useState(emptyBranding);
  const [localError, setLocalError] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createStatus, setCreateStatus] = React.useState("");
  const [soundEnabled, setSoundEnabled] = React.useState(() => isOnboardingSoundEnabled());

  const ownedCount = Number(projectCreation?.ownedCount ?? (superAdmin ? 0 : projects.length));
  const canCreateProject = superAdmin || Boolean(
    projectCreation?.canCreate ?? (!superAdmin && projects.length === 0),
  );
  const showCreatePanel = superAdmin || canCreateProject;
  const creationReason = String(projectCreation?.reason || "");
  const subscriptionTarget = projectCreation?.subscriptionProject || activeProject || projects[0] || null;
  const firstProjectMode = !superAdmin && projects.length === 0 && canCreateProject;

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    setForm((previous) => ({
      ...previous,
      ownerEmail: previous.ownerEmail || user?.email || "",
    }));
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => {
      const next = { ...previous, [name]: value };
      if (name === "name") next.slug = makeSlug(value);
      return next;
    });
  };

  const handleBrandingChange = (key, file) => {
    setBranding((previous) => ({ ...previous, [key]: file }));
  };

  const handleSoundToggle = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    setOnboardingSoundEnabled(next);
    if (next) playOnboardingSound("start");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let project = null;

    try {
      playOnboardingSound("start");
      setCreating(true);
      setCreateStatus("Creando el proyecto y su espacio privado...");
      setLocalError("");

      project = await createProject({
        name: form.name,
        slug: form.slug,
        ownerEmail: form.ownerEmail,
        plan: form.plan,
        domains: form.domains,
        modules: modulesForPlan(form.plan),
      });

      setCreateStatus("Proyecto creado. Render está preparando la página pública...");

      const hasBranding = Boolean(branding.logo || branding.compactLogo || branding.favicon);
      if (hasBranding && project?.slug) {
        setCreateStatus("Subiendo tus logos y recursos visuales...");
        await uploadInitialBranding(project.slug, branding, form.name);
      }

      const completedPlan = form.plan;
      setForm(emptyForm(user?.email || "", superAdmin ? "catalogo" : "landing"));
      setBranding(emptyBranding);
      playOnboardingSound("success");

      if (project?.slug) {
        const firstPath = hasBranding || completedPlan === "landing" ? "pagina" : "productos";
        navigate(`/dashboard/proyectos/${project.slug}/${firstPath}`);
      }
    } catch (err) {
      if (project?.slug) {
        setLocalError(
          `El proyecto ${project.name || project.slug} fue creado, pero no se pudo completar la identidad visual: ${err.message || "error desconocido"}. Podés reintentar desde Landing > Ajustes > Recursos visuales.`
        );
        await loadProjects();
      } else {
        setLocalError(err.message || "No se pudo crear el proyecto");
      }
    } finally {
      setCreating(false);
      setCreateStatus("");
    }
  };

  if (firstProjectMode) {
    return (
      <FirstProjectOnboarding
        form={form}
        branding={branding}
        creating={creating}
        createStatus={createStatus}
        error={localError || error}
        soundEnabled={soundEnabled}
        onSoundToggle={handleSoundToggle}
        onChange={handleChange}
        onBrandingChange={handleBrandingChange}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
          Proyectos
        </Typography>
        <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
          {superAdmin
            ? "Acá das de alta clientes. Cada proyecto apunta a una DB y un espacio de archivos propios."
            : "Administrá tus proyectos y revisá cuándo podés sumar uno nuevo."}
        </Typography>
      </Box>

      {(error || localError) && <Alert severity="error" onClose={() => setLocalError("")}>{localError || error}</Alert>}

      {!superAdmin && !canCreateProject && creationReason === "subscription_required" ? (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: "1px solid rgba(243,90,58,.22)", bgcolor: "rgba(243,90,58,.045)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 42, height: 42, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.12)", color: BRAND.orange, flexShrink: 0 }}>
                <WorkspacePremiumRoundedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Tu primer proyecto ya está creado
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.66)", maxWidth: 720 }}>
                  Para agregar un segundo proyecto necesitás activar una suscripción en tu proyecto actual. La suscripción habilita un proyecto adicional sin modificar el que ya tenés.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              disabled={!subscriptionTarget?.slug}
              onClick={() => subscriptionTarget?.slug && navigate(`/dashboard/proyectos/${subscriptionTarget.slug}/configuracion/suscripcion`)}
              sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, px: 2.5, flexShrink: 0 }}
            >
              Activar suscripción
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {!superAdmin && !canCreateProject && creationReason === "limit_reached" ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Tu cuenta ya utiliza los {projectCreation?.maxProjects || 2} proyectos disponibles: el primero incluido y uno adicional habilitado por la suscripción.
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        {showCreatePanel && (
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                  <AddRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                    {superAdmin ? "Nuevo cliente" : "Agregar otro proyecto"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>
                    {superAdmin
                      ? "Proyecto, dominio e identidad inicial en un solo flujo."
                      : "Tu suscripción habilitó un proyecto adicional para esta cuenta."}
                  </Typography>
                </Box>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField label="Nombre del negocio" name="name" value={form.name} onChange={handleChange} required fullWidth />
                  <TextField label="Slug" name="slug" value={form.slug} onChange={handleChange} required fullWidth helperText="Ej: repuestos-rr → DB: wp_repuestos_rr" />
                  {superAdmin ? (
                    <TextField label="Email owner" name="ownerEmail" type="email" value={form.ownerEmail} onChange={handleChange} required fullWidth />
                  ) : null}
                  {superAdmin ? (
                    <TextField select label="Plan" name="plan" value={form.plan} onChange={handleChange} fullWidth>
                      <MenuItem value="landing">Landing</MenuItem>
                      <MenuItem value="catalogo">Catálogo</MenuItem>
                      <MenuItem value="pedidos">Pedidos</MenuItem>
                      <MenuItem value="pro">Pro</MenuItem>
                      <MenuItem value="plus">Plus</MenuItem>
                    </TextField>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "rgba(25,36,47,.025)" }}>
                      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)", fontWeight: 900 }}>
                        INICIO DEL PROYECTO
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontWeight: 900, color: BRAND.dark }}>
                        Landing editable
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, color: "rgba(25,36,47,.62)" }}>
                        Primero creás la página. Después activás catálogo, pedidos, cobros u otros módulos desde Tu plan.
                      </Typography>
                    </Paper>
                  )}
                  <TextField
                    label="Dominio público"
                    name="domains"
                    value={form.domains}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={2}
                    helperText="Opcional. Uno por línea o separados por coma. Podés pegar https://cliente.com y el backend guardará sólo el hostname."
                  />

                  <Divider />
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Identidad inicial</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(25,36,47,.60)", mt: 0.25 }}>
                      Opcional. Después se puede reemplazar desde el editor visual sin tocar código.
                    </Typography>
                  </Box>
                  <PendingAssetField
                    label="Logo principal"
                    file={branding.logo}
                    disabled={creating}
                    helperText="SVG, PNG, WebP o JPG. Se guarda en el espacio del cliente."
                    onChange={(file) => handleBrandingChange("logo", file)}
                  />
                  <PendingAssetField
                    label="Logo compacto"
                    file={branding.compactLogo}
                    disabled={creating}
                    helperText="Versión cuadrada o reducida para navbar móvil."
                    onChange={(file) => handleBrandingChange("compactLogo", file)}
                  />
                  <PendingAssetField
                    label="Favicon"
                    file={branding.favicon}
                    disabled={creating}
                    accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                    helperText="Preferentemente cuadrado. SVG, PNG, WebP o ICO."
                    onChange={(file) => handleBrandingChange("favicon", file)}
                  />

                  {createStatus ? <Alert severity="info">{createStatus}</Alert> : null}
                  <Button type="submit" disabled={creating} variant="contained" size="large" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                    {creating
                      ? "Preparando proyecto..."
                      : superAdmin
                        ? "Crear proyecto"
                        : ownedCount === 0
                          ? "Crear mi primer proyecto"
                          : "Agregar proyecto"}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={showCreatePanel ? 7 : 12}>
          <Stack spacing={2}>
            {loading && <Alert severity="info">Cargando proyectos...</Alert>}
            {projects.map((projectItem) => {
              const isActive = activeProject?.slug === projectItem.slug;
              const modules = normalizeProjectModules(projectItem.modules);
              const moduleLabels = activeModuleLabels(projectItem);
              return (
                <Card key={projectItem.id || projectItem.slug} elevation={0} sx={{ borderRadius: 4, border: `1px solid ${isActive ? BRAND.orange : "rgba(25,36,47,0.08)"}` }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                            {projectItem.name}
                          </Typography>
                          {isActive && <Chip label="Activo" size="small" sx={{ bgcolor: "rgba(243,90,58,0.12)", color: BRAND.orange, fontWeight: 900 }} />}
                        </Stack>
                        <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.75 }}>
                          /{projectItem.slug} · DB: {projectItem.dbName} · Plan: {BILLING_PLAN_LABELS[normalizeProjectPlanCode(projectItem.billing?.planCode || projectItem.plan)]}
                        </Typography>
                        {projectItem.domains?.length ? (
                          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)", mt: 0.45 }}>
                            Dominios asociados: {projectItem.domains.join(", ")}
                          </Typography>
                        ) : null}
                        <Box sx={{ mt: 1.5, maxWidth: 760 }}>
                          <ProjectDeploymentCard project={projectItem} compact />
                        </Box>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          <Chip size="small" label={`Billing: ${projectItem.billing?.status || "trial"}`} color={["active", "trial", "exempt"].includes(projectItem.billing?.status || "trial") ? "success" : "warning"} />
                          <Chip size="small" label={`Página: ${projectItem.status || "active"}`} variant="outlined" />
                        </Stack>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                          {moduleLabels.length > 0 ? moduleLabels.map((label) => (
                            <Chip key={label} size="small" label={label} variant="outlined" />
                          )) : <Chip size="small" label="Sin módulos" />}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant={isActive ? "contained" : "outlined"} onClick={() => setActiveProject(projectItem)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, bgcolor: isActive ? BRAND.orange : "transparent", borderColor: "rgba(25,36,47,0.18)" }}>
                          Usar
                        </Button>
                        {superAdmin && (
                          <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/general`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Administrar
                          </Button>
                        )}
                        <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/suscripcion`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.orange }}>
                          Plan
                        </Button>
                        {modules.landing && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pagina`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Landing
                          </Button>
                        )}
                        {modules.products && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/productos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Productos
                          </Button>
                        )}
                        {modules.orders && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pedidos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Pedidos
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
